import { useEffect } from 'react';
import Select, { createFilter } from 'react-select';
import CreatableSelect from 'react-select/creatable';

import { WerklijnContextMenuDetails } from '@/components/contextMenu/mwdContextMenu/types';
import MwdDialog from '@/components/UI/Dialog/Dialog';
import FieldGroup from '@/components/UI/Fields/FieldGroup';
import useFormHtml from '@/hooks/useFormHtml';
import { reactSelectOnBlurWorkaround } from '@/utils/helpers';

import Input from '../../UI/Fields/Input';

import { ClaimFormType } from './types';
import useClaimDialog from './useClaimDialog';

import styles from '@/components/UI/Dialog/Dialog.module.css';

export type ClaimDialogData = {
  details: WerklijnContextMenuDetails;
};

export type ClaimDialogProps = {
  showDialog: boolean;
  dienstregelpunten: string[];
  handleOpenChange: (p: boolean) => void;
  werklijnGegevens: ClaimDialogData;
  isUpdate?: boolean;
};

const ClaimDialog = ({
  dienstregelpunten,
  showDialog,
  handleOpenChange,
  werklijnGegevens,
  isUpdate = false,
}: ClaimDialogProps) => {
  const {
    claimState,
    dispatch,
    claimFormEvent,
    claimsoorten,
    dateVariations,
    resetDialog,
    claimsoortOptionValue,
    warningMessage,
  } = useClaimDialog(werklijnGegevens.details, handleOpenChange);
  const { formSubmitCallback } = useFormHtml<ClaimFormType>(claimFormEvent);
  const createOption = (label: string) => ({
    label,
    value: label,
  });
  // reset dialog als dialog gesloten wordt via een outsideClick
  useEffect(() => {
    if (!showDialog) resetDialog(false);
  }, [showDialog]);

  return (
    <MwdDialog
      title={isUpdate ? 'Claim' : 'Claim toevoegen'}
      className={styles.claimDialogStyle}
      showDialog={showDialog}
      handleOpenChange={handleOpenChange}
      data-testid={`contextMenu-${werklijnGegevens?.details?.naInzetId}`}
      hasCloseInHeader={true}
      showNotification={warningMessage}
      notificationType='warning'
      notificationText='Let op: de werklijnversie is gewijzigd'
    >
      <form onSubmit={formSubmitCallback}>
        <div style={{ marginTop: '4px', marginBottom: '4px', gap: '12px', display: 'flex', flexDirection: 'row' }}>
          <div style={{ width: 'var(--col-md)' }}>
            <FieldGroup.Col
              required
              fieldId='vertrek'
              label='Dienstregelpunt vertrek'
              subLabel='De locatie van het materieel'
              errorMessage={claimState.fieldErrors?.vertrek}
            >
              <div data-testid='claim-dialoog-vertrek'>
                <CreatableSelect
                  placeholder='Dienstregelpunt vertrek'
                  className='react-select-container'
                  formatCreateLabel={(inputValue) => `Dienstregelpunt: ${inputValue}`} // NOSONAR
                  classNamePrefix='react-select'
                  isClearable={!!claimState.eigenschappen.vertrek}
                  isSearchable
                  filterOption={createFilter({ matchFrom: 'start' })}
                  escapeClearsValue
                  inputId='vertrek'
                  name='vertrek'
                  // data-testid='claim-dialoog-aankomst'
                  onChange={(event) => {
                    dispatch({
                      type: 'update',
                      value: event?.value,
                      field: 'vertrek',
                    });
                  }}
                  value={
                    dienstregelpunten
                      ? createOption(
                          dienstregelpunten.find((opt: string) => opt === claimState.eigenschappen.vertrek) ??
                            claimState.eigenschappen.vertrek,
                        )
                      : []
                  }
                  options={dienstregelpunten ? dienstregelpunten.map((item) => createOption(item)) : []}
                  onBlur={reactSelectOnBlurWorkaround}
                />
              </div>
            </FieldGroup.Col>
          </div>
          <div style={{ width: 'var(--col-md)' }}>
            <FieldGroup.Col fieldId='aankomst' label='Naar dienstregelpunt' subLabel='Laat leeg voor materieelclaim'>
              <div data-testid='claim-dialoog-aankomst'>
                <CreatableSelect
                  placeholder='Zoek dienstregelpunt'
                  formatCreateLabel={(inputValue) => `Dienstregelpunt: ${inputValue}`} // NOSONAR
                  isSearchable
                  isClearable={!!claimState.eigenschappen.aankomst}
                  escapeClearsValue
                  filterOption={createFilter({ matchFrom: 'start' })}
                  className='react-select-container'
                  classNamePrefix='react-select'
                  inputId='aankomst'
                  name='aankomst'
                  onChange={(event) =>
                    dispatch({
                      type: 'update',
                      value: event?.value,
                      field: 'aankomst',
                    })
                  }
                  value={
                    dienstregelpunten
                      ? createOption(
                          dienstregelpunten.find((opt: string) => opt === claimState.eigenschappen.aankomst) ??
                            claimState.eigenschappen.aankomst ??
                            '',
                        )
                      : []
                  }
                  options={dienstregelpunten ? dienstregelpunten.map((item) => createOption(item)) : []}
                  onBlur={reactSelectOnBlurWorkaround}
                />
              </div>
            </FieldGroup.Col>
          </div>
        </div>
        <div style={{ marginTop: '4px', marginBottom: '4px', gap: '12px', display: 'flex', flexDirection: 'row' }}>
          <div style={{ width: 'var(--col-md)' }}>
            <FieldGroup.Col
              required
              fieldId='beginDatumLabel'
              label='Van datum'
              errorMessage={claimState.fieldErrors?.beginDatum}
            >
              <Select
                placeholder='Van datum...'
                className='react-select-container'
                classNamePrefix='react-select'
                inputId='beginDatumLabel'
                name='beginDatum'
                value={dateVariations.filter((opt) => opt.value === claimState.beginDatum)}
                onChange={(value) => {
                  dispatch({ type: 'updateDateTime', field: 'beginDatum', value: value?.value });
                }}
                options={dateVariations}
                onBlur={reactSelectOnBlurWorkaround}
              />
            </FieldGroup.Col>
          </div>
          <div style={{ width: 'var(--col-md)' }}>
            <FieldGroup.Col
              required
              fieldId='eindDatumLabel'
              label='Tot datum'
              errorMessage={claimState.fieldErrors?.eindDatum}
            >
              <Select
                required
                placeholder='Tot datum...'
                className='react-select-container'
                classNamePrefix='react-select'
                inputId='eindDatumLabel'
                name='eindDatum'
                value={dateVariations.filter((opt) => opt.value === claimState.eindDatum)}
                onChange={(value) => {
                  dispatch({ type: 'updateDateTime', field: 'eindDatum', value: value?.value });
                }}
                options={dateVariations}
                onBlur={reactSelectOnBlurWorkaround}
              />
            </FieldGroup.Col>
          </div>
        </div>
        <div style={{ marginTop: '4px', marginBottom: '4px', display: 'flex', gap: '4px' }}>
          <div style={{ width: 'var(--col-md)' }}>
            <FieldGroup.Col
              required
              fieldId='beginTijd'
              FieldComponent={Input.Time}
              label='Van tijd'
              defaultValue={claimState.beginTijd}
              onChange={(e) => dispatch({ type: 'updateDateTime', field: 'beginTijd', value: e.currentTarget.value })}
              errorMessage={claimState.fieldErrors?.beginTijd}
            />
          </div>
          <div style={{ width: 'var(--col-md)' }}>
            <FieldGroup.Col
              required
              fieldId='eindTijd'
              FieldComponent={Input.Time}
              label='Tot tijd'
              defaultValue={claimState.eindTijd}
              onChange={(e) => dispatch({ type: 'updateDateTime', field: 'eindTijd', value: e.currentTarget.value })}
              errorMessage={claimState.fieldErrors?.eindTijd}
            />
          </div>
        </div>

        <div
          style={{
            height: '2px',
            marginBottom: '14px',
            marginTop: '14px',
            width: '100%',
            background: 'var(--tractie-color-border-subtle)',
          }}
        />
        <div style={{ marginTop: '4px', marginBottom: '4px', display: 'flex', gap: '12px' }}>
          <div style={{ width: '60%' }}>
            <FieldGroup.Col fieldId='claimsoortLabel' label='Claimsoort'>
              <Select
                id='normal-select-claimsoortLabel'
                className='react-select-container'
                classNamePrefix='react-select'
                placeholder='Zoek claimsoort'
                inputId='claimsoortLabel'
                name='claimsoort'
                value={claimsoortOptionValue}
                onChange={(value) => {
                  dispatch({ type: 'update', field: 'claimsoort', value: value?.value });
                }}
                options={claimsoorten}
                onBlur={reactSelectOnBlurWorkaround}
              />
            </FieldGroup.Col>
          </div>
          <div style={{ width: '40%' }}>
            <FieldGroup.Col
              fieldId='bijzonderheidscode'
              FieldComponent={Input.Text}
              maxLength={6}
              label='Notitie bij werklijn'
              defaultValue={claimState.eigenschappen.bijzonderheidscode ?? ''}
              onChange={(event) =>
                dispatch({ type: 'update', value: event.currentTarget.value, field: 'bijzonderheidscode' })
              }
            />
          </div>
        </div>
        <FieldGroup.Col
          fieldId='toelichting'
          FieldComponent={Input.TextArea}
          label='Notitie'
          rows={5}
          maxLength={255}
          defaultValue={claimState.eigenschappen.toelichting ?? ''}
          onChange={(event) => dispatch({ type: 'update', value: event.currentTarget.value, field: 'toelichting' })}
        />

        <MwdDialog.Footer
          submitCaption={isUpdate ? 'Wijzig claim' : 'Toevoegen claim'}
          cancelCallback={() => resetDialog(true)}
        />
      </form>
    </MwdDialog>
  );
};
export default ClaimDialog;
